import { gql, useSubscription } from '@apollo/client'
import { captureException } from '@sentry/react'
import moment from 'moment-timezone'
import { createContext, useMemo } from 'react'

import Spinner from 'src/components/common/spinner'
import { APP_ENV } from 'src/env'
import BUILD_METADATA from 'src/metadata.json' with { type: 'json' }
import logger from 'src/utils/logger'

const VERSION = `${BUILD_METADATA.buildMajor}.${BUILD_METADATA.buildMinor}.${BUILD_METADATA.buildRevision}`
const RUNNING_BUILD_ID = VERSION
const IS_V4 = APP_ENV.REACT_APP_ENVIRONMENT === 'v4'
const IS_LOCAL = APP_ENV.REACT_APP_LOCAL === 'yes'

/*

We should reload the page under following conditions only

- there are no query Errors
- latestBuildID is a string returned from the subscription response
- we are not running a cloned version of an environment i.e. V4 dashboard
- we are not running locally
- currentBuildID !== latestBuildID

This would help resolve the following conditions

- An error occurred where we weren't able to fetch the data from Hasura due to permissions
  on the table/column or due to authn/authz from the Hasura auth webhook
- latestBuildID table doesn't contain any rows
- we are on the v4 dashboard in which case the IDs will never match
- finally we only ever reload when the running and latest buildIDs are different

*/

const BUILD_SUBSCRIPTION = gql`
    subscription BUILD_SUBSCRIPTION {
        builds: master_ornaBuildHistory(
            where: { enabled: { _eq: true } }
            order_by: { buildTime: desc }
            limit: 1
        ) {
            id
            commit
            buildTime
        }
    }
`

/**
 * @typedef {Object} BuildCtx
 * @prop {boolean} SHOW_RELOAD_BANNER
 * @prop {string|null} CURRENT_BUILD_ID
 * @prop {string} RUNNING_BUILD_ID
 * @prop {import('moment').Moment|null} CURRENT_BUILD_TIME
 */

/**
 * @type {ReturnType<typeof createContext<BuildCtx>>}
 */
const BuildContext = createContext()

export default BuildContext

BuildContext.displayName = 'BuildContext'

export const BuildContextProvider = ({ children }) => {
    const {
        error,
        loading,
        data: { builds } = {},
    } = useSubscription(BUILD_SUBSCRIPTION)

    /**
     * Latest build ID from the DB
     *
     * @type {string|null}
     */
    const CURRENT_BUILD_ID = useMemo(() => {
        if (Array.isArray(builds) && builds.length > 0) {
            return builds[0].id
        }

        return null
    }, [builds])

    /**
     * Current active build time
     *
     * @type {import('moment').Moment|null}
     */
    const CURRENT_BUILD_TIME = useMemo(() => {
        if (Array.isArray(builds) && builds.length > 0) {
            return moment.unix(builds[0].buildTime)
        }

        return null
    }, [builds])

    /**
     * Whether a reload banner should be shown
     *
     * @type {boolean}
     */
    const SHOW_RELOAD_BANNER = useMemo(() => {
        if (
            typeof CURRENT_BUILD_ID === 'string' &&
            !IS_LOCAL &&
            !IS_V4 &&
            CURRENT_BUILD_ID !== RUNNING_BUILD_ID
        ) {
            return true
        }

        return false
    }, [CURRENT_BUILD_ID])

    if (loading) return <Spinner />

    if (error) {
        logger.error('[BuildProvider] Error fetching build data')
        logger.error(error.stack)
        captureException(error, {
            extra: {
                message: 'Error fetching build ID',
            },
        })
        return (
            <BuildContext.Provider
                value={{
                    SHOW_RELOAD_BANNER: false,
                    CURRENT_BUILD_ID,
                    CURRENT_BUILD_TIME,
                    RUNNING_BUILD_ID,
                }}
            >
                {children}
            </BuildContext.Provider>
        )
    }

    return (
        <BuildContext.Provider
            value={{
                CURRENT_BUILD_ID,
                CURRENT_BUILD_TIME,
                RUNNING_BUILD_ID,
                SHOW_RELOAD_BANNER,
            }}
        >
            {children}
        </BuildContext.Provider>
    )
}
